import { motion } from "framer-motion";
import { whatsappUrl } from "@/lib/whatsapp";
import { Sprout, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

// Anuncio de la escuela infantil 1-3 (apertura otoño 2026). Retirar o convertir
// en página propia cuando la etapa esté en marcha.

const T = {
  es: {
    badge: "Muy pronto",
    title: "Escuela infantil de 1 a 3 años",
    text: "Este otoño abrimos una nueva etapa para peques de 1 a 3 años, con el mismo cuidado y el mismo proyecto de siempre. Las plazas son muy limitadas y ya puedes reservar la tuya.",
    cta: "Reservar plaza",
    wa: "¡Hola! Quiero reservar plaza en la nueva escuela infantil de 1 a 3 años.",
  },
  en: {
    badge: "Coming soon",
    title: "Nursery for ages 1 to 3",
    text: "This autumn we are opening a new stage for children aged 1 to 3, with the same care and the same project as always. Places are very limited and you can already reserve yours.",
    cta: "Reserve a place",
    wa: "Hi! I would like to reserve a place at the new nursery for ages 1 to 3.",
  },
  pt: {
    badge: "Muito em breve",
    title: "Creche de 1 a 3 anos",
    text: "Neste outono abrimos uma nova etapa para crianças de 1 a 3 anos, com o mesmo cuidado e o mesmo projeto de sempre. As vagas são muito limitadas e já pode reservar a sua.",
    cta: "Reservar vaga",
    wa: "Olá! Gostaria de reservar uma vaga na nova creche de 1 a 3 anos.",
  },
  fr: {
    badge: "Très bientôt",
    title: "Crèche de 1 à 3 ans",
    text: "Cet automne, nous ouvrons une nouvelle étape pour les enfants de 1 à 3 ans, avec le même soin et le même projet que toujours. Les places sont très limitées et vous pouvez déjà réserver la vôtre.",
    cta: "Réserver une place",
    wa: "Bonjour ! Je souhaite réserver une place à la nouvelle crèche de 1 à 3 ans.",
  },
} as const;

export const Nursery13Banner = () => {
  const { language } = useLanguage();
  const t = T[language as keyof typeof T] || T.es;
  const whatsapp = whatsappUrl(t.wa);

  return (
    <section className="bg-accent/10 border-y border-accent/20">
      <div className="container mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row items-center gap-6 md:gap-10 max-w-4xl mx-auto text-center md:text-left"
        >
          <Sprout className="w-12 h-12 text-primary flex-shrink-0" />
          <div className="flex-1">
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-primary mb-1">
              {t.badge}
            </span>
            <h2 className="text-2xl font-light text-primary mb-2">{t.title}</h2>
            <p className="text-muted-foreground text-sm">{t.text}</p>
          </div>
          <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6 py-5 flex-shrink-0">
            <a href={whatsapp} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-5 h-5 mr-2" /> {t.cta}
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
